import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import GlossaryGraph, { type GraphNode } from "../components/GlossaryGraph";
import { useDebouncedValue } from "../lib/hooks";
import { trpc } from "../trpc";

function GraphSearch({ onFocus }: { onFocus: (slug: string) => void }) {
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query.trim());
  const results = trpc.glossary.search.useQuery(
    { q: debounced, limit: 8 },
    { enabled: debounced.length > 0 },
  );
  return (
    <div className="w-64">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Find a word…"
        className="w-full rounded-xl border border-line-strong bg-paper-raised/95 px-4 py-2 text-sm text-ink shadow-sm outline-none backdrop-blur focus:border-terracotta/60"
        aria-label="Find a word in the graph"
      />
      {debounced.length > 0 && results.data && results.data.length > 0 ? (
        <ul className="mt-1.5 max-h-72 overflow-auto rounded-xl border border-line bg-paper-raised/95 shadow-md backdrop-blur">
          {results.data.map((t) => (
            <li key={t.slug}>
              <button
                type="button"
                onClick={() => {
                  onFocus(t.slug);
                  setQuery("");
                }}
                className="block w-full px-4 py-2 text-left font-serif text-ink transition-colors hover:bg-paper-sunk"
              >
                {t.displayName}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function GraphPage() {
  const [params, setParams] = useSearchParams();
  const focus = params.get("focus");
  const [selected, setSelected] = useState<GraphNode | null>(null);
  const [showDohas, setShowDohas] = useState(false);

  const full = trpc.glossary.fullGraph.useQuery(
    { includeDohas: showDohas },
    { enabled: !focus, staleTime: 5 * 60_000 },
  );
  const ego = trpc.glossary.graph.useQuery(
    { focusSlug: focus ?? undefined, limit: 60 },
    { enabled: Boolean(focus) },
  );

  const active = focus ? ego : full;
  const data = active.data;

  const setFocus = (slug: string | null) => {
    setSelected(null);
    setParams(slug ? { focus: slug } : {});
  };

  const onSelectNode = (node: GraphNode) => {
    setSelected(node);
    if (node.type === "term") setFocus(node.slug);
  };

  return (
    <div className="relative h-[calc(100vh-4rem)] w-full overflow-hidden">
      {/* The graph fills the whole stage. */}
      {active.isLoading ? (
        <div className="flex h-full items-center justify-center gap-3 text-ink-faint">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-terracotta" />
          <span className="text-sm">
            {focus ? "Gathering its neighbours…" : "Weaving the whole web…"}
          </span>
        </div>
      ) : (
        <GlossaryGraph
          nodes={data?.nodes ?? []}
          edges={data?.edges ?? []}
          focusId={focus ? `t:${focus}` : null}
          onSelectNode={onSelectNode}
          className="h-full w-full"
        />
      )}

      {/* Title + search — top left */}
      <div className="pointer-events-none absolute left-5 top-5 max-w-xs rounded-2xl border border-line bg-paper-raised/80 p-4 shadow-sm backdrop-blur-md">
        <p className="eyebrow">The graph</p>
        <h1 className="mt-1 font-serif text-3xl text-ink">
          {focus ? "Around this word" : "The whole web"}
        </h1>
        <p className="mt-1 text-xs leading-relaxed text-ink-soft">
          {focus
            ? "Its neighbours and the dohas it lives in. Click a word to walk on."
            : `${data ? data.nodes.length.toLocaleString() : "—"} words, every connection between them. Drag to roam, scroll to zoom, click a word to enter it.`}
        </p>
        <div className="pointer-events-auto mt-3">
          <GraphSearch onFocus={setFocus} />
        </div>
      </div>

      {/* Controls — top right */}
      <div className="absolute right-5 top-5 flex flex-col items-end gap-2">
        {focus ? (
          <button
            type="button"
            onClick={() => setFocus(null)}
            className="rounded-full border border-line-strong bg-paper-raised/90 px-4 py-2 text-sm font-medium text-ink shadow-sm backdrop-blur transition-colors hover:bg-paper-sunk"
          >
            ← Back to the whole
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setShowDohas((v) => !v)}
            className={[
              "rounded-full border px-4 py-2 text-sm font-medium shadow-sm backdrop-blur transition-colors",
              showDohas
                ? "border-terracotta/50 bg-terracotta-wash/80 text-terracotta"
                : "border-line-strong bg-paper-raised/90 text-ink hover:bg-paper-sunk",
            ].join(" ")}
          >
            {showDohas ? "Hide dohas" : "Show dohas"}
          </button>
        )}

        {selected ? (
          <div className="w-60 rounded-2xl border border-line bg-paper-raised/95 p-4 shadow-lg backdrop-blur">
            <p className="eyebrow">
              {selected.type === "term" ? "Word" : "Doha"}
            </p>
            <p className="mt-1 font-serif text-2xl text-ink">
              {selected.label}
            </p>
            <Link
              to={
                selected.type === "term"
                  ? `/glossary/${selected.slug}`
                  : `/doha/${selected.slug}`
              }
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-terracotta"
            >
              Open {selected.type === "term" ? "the word" : "the doha"} →
            </Link>
          </div>
        ) : null}
      </div>

      {/* Legend — bottom left */}
      <div className="absolute bottom-5 left-5 flex items-center gap-4 rounded-full border border-line bg-paper-raised/90 px-4 py-2 text-xs text-ink-soft backdrop-blur">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-ink-soft" /> word
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-terracotta" /> doha
        </span>
      </div>
    </div>
  );
}
